/**
 * expense-group-by-utils.ts
 *
 * Grouping logic and subtotal calculations for the Expense Entries Tab.
 *
 * Supported group-by fields (categorical / low-cardinality only):
 *   - status        (Expense.approved → 'Approved' | 'Pending')
 *   - employee      (Expense.employeeId — resolved display name)
 *   - type          (Expense.category — expense type, e.g. Travel, Subsistence)
 *   - category      (Expense.paymentMethod — Credit Card or Expense)
 *
 * Subtotals per group:
 *   - count           number of expense entries
 *   - totalAmount     sum of amounts across all entries in group
 *   - approvedAmount  sum of amounts for approved entries
 *   - pendingAmount   sum of amounts for pending entries
 */

import type { Expense } from '@/types';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type EXGroupById =
  | 'status'
  | 'employee'
  | 'type'
  | 'category';

export interface EXGroupByOption {
  id: EXGroupById;
  label: string;
}

export const EX_GROUP_BY_OPTIONS: EXGroupByOption[] = [
  { id: 'status',   label: 'Status'   },
  { id: 'employee', label: 'Employee' },
  { id: 'type',     label: 'Type'     },
  { id: 'category', label: 'Category' },
];

export interface EXGroupSubtotals {
  count: number;
  totalAmount: number;
  approvedAmount: number;
  pendingAmount: number;
}

export interface ExpenseGroup {
  /** Stable key used for collapse state */
  key: string;
  /** Human-readable label shown in the group header */
  label: string;
  /** The EXGroupById field this group was created from */
  groupById: EXGroupById;
  expenses: Expense[];
  subtotals: EXGroupSubtotals;
}

// ─── Key extraction ────────────────────────────────────────────────────────────

export function getEXGroupKey(
  expense: Expense,
  groupBy: EXGroupById,
  resolveEmployeeName: (expense: Expense) => string,
): string {
  switch (groupBy) {
    case 'status':
      return expense.approved ? 'Approved' : 'Pending';

    case 'employee':
      return resolveEmployeeName(expense) || '__none__';

    case 'type':
      return expense.category || '__none__';

    case 'category':
      return expense.paymentMethod || '__none__';

    default:
      return '__none__';
  }
}

function getGroupLabel(key: string, groupBy: EXGroupById): string {
  if (key === '__none__') {
    switch (groupBy) {
      case 'category': return 'Unspecified';
      default:         return 'Unknown';
    }
  }
  return key;
}

// ─── Subtotal calculation ──────────────────────────────────────────────────────

function calcSubtotals(expenses: Expense[]): EXGroupSubtotals {
  return expenses.reduce<EXGroupSubtotals>(
    (acc, e) => {
      acc.count          += 1;
      acc.totalAmount    += e.amount;
      acc.approvedAmount += e.approved ? e.amount : 0;
      acc.pendingAmount  += e.approved ? 0 : e.amount;
      return acc;
    },
    { count: 0, totalAmount: 0, approvedAmount: 0, pendingAmount: 0 },
  );
}

// ─── Sort order for group headers ─────────────────────────────────────────────

// Status: Pending first (action required), then Approved
const STATUS_ORDER: Record<string, number> = {
  Pending:  0,
  Approved: 1,
};

function groupSortKey(group: ExpenseGroup, groupBy: EXGroupById): string {
  if (group.key === '__none__') return 'zzz';
  switch (groupBy) {
    case 'status':
      return String(STATUS_ORDER[group.key] ?? 99).padStart(2, '0');
    default:
      return group.label.toLowerCase();
  }
}

// ─── Main grouping function ────────────────────────────────────────────────────

/**
 * Buckets an array of Expense objects into groups for the given Group By field.
 */
export function groupExpenses(
  expenses: Expense[],
  groupBy: EXGroupById,
  resolveEmployeeName: (expense: Expense) => string,
): ExpenseGroup[] {
  const map = new Map<string, Expense[]>();

  for (const expense of expenses) {
    const key = getEXGroupKey(expense, groupBy, resolveEmployeeName);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(expense);
  }

  const groups: ExpenseGroup[] = [];

  for (const [rawKey, groupExpenses] of map.entries()) {
    groups.push({
      key:       rawKey,
      label:     getGroupLabel(rawKey, groupBy),
      groupById: groupBy,
      expenses:  groupExpenses,
      subtotals: calcSubtotals(groupExpenses),
    });
  }

  groups.sort((a, b) =>
    groupSortKey(a, groupBy).localeCompare(groupSortKey(b, groupBy)),
  );

  return groups;
}
